/**
 * App-wide scope filter shared by every list/stats endpoint.
 *
 * A "scope" narrows a query to a single origin country and/or vertical.
 * The UI surfaces "global" (all countries) and "all" (all verticals,
 * consolidated) sentinels; engine + helm both normalize them through
 * `parseScope` so the absence of a filter is always `undefined`.
 */

/** Country sentinel meaning "all countries" (no country filter). */
export const GLOBAL_COUNTRY = "global"
/** Vertical sentinel meaning "all verticals" (consolidated, no filter). */
export const ALL_VERTICALS = "all"

export interface ScopeQuery {
	/** 2-char country code, or undefined for all countries. */
	country?: string
	/** vertical id, or undefined for all verticals (consolidated). */
	vertical?: string
}

/** Trim a raw value; map empty/sentinel matches (case-insensitive) to undefined. */
const normalize = (raw: string | null | undefined, sentinel: string): string | undefined => {
	const trimmed = raw?.trim()
	if (!trimmed || trimmed.toLowerCase() === sentinel) return undefined
	return trimmed
}

/** Treat the "global"/"all"/empty sentinels (and whitespace) as "no filter". */
export function parseScope(raw: {
	country?: string | null
	vertical?: string | null
}): ScopeQuery {
	const scope: ScopeQuery = {}
	const country = normalize(raw.country, GLOBAL_COUNTRY)
	if (country !== undefined) scope.country = country
	const vertical = normalize(raw.vertical, ALL_VERTICALS)
	if (vertical !== undefined) scope.vertical = vertical
	return scope
}

/** True when neither dimension filters (the global, consolidated view). */
export function isGlobalScope(scope: ScopeQuery): boolean {
	return !scope.country && !scope.vertical
}
